/**
 * buses.ts — the MIX CONTRACT of the shared audio runtime (2026-09-20). PURE:
 * no Web Audio, no DOM — the harness runs it in node.
 *
 * Atlas had six independent sound owners, each with its own AudioContext and
 * nothing in common but a mute switch. This file is the vocabulary they now
 * share: the category BUSES, how a legacy clip KEY finds its bus and priority,
 * the user-facing VOLUME GROUPS, and the per-tier BUDGETS.
 *
 * ROUTES. A bus leaves the graph one of three ways:
 *   game       → the game gate (the intro silences it) → the master
 *   voice      → the master, AROUND the game gate — a live call is the
 *                player's choice (the documented exception, now explicit)
 *   cinematic  → the master, around the game gate — the intro must never be
 *                silenced by the switch it throws itself
 * Nothing reaches the speakers except through the master.
 *
 * WORLD buses additionally pass the world sub-mix (the underwater low-pass, the
 * room reverb send). UI, music, dialogue, voice chat and the beds do not: a
 * warning must stay readable under water, and a bed swap already IS the medium.
 */

export type Bus =
  | 'ui' | 'music' | 'ambience' | 'weather' | 'playerFoley' | 'npcFoley'
  | 'combat' | 'vehicles' | 'machinery' | 'dialogue' | 'cinematic' | 'voice';

export const BUSES: readonly Bus[] = ['ui', 'music', 'ambience', 'weather', 'playerFoley', 'npcFoley', 'combat', 'vehicles', 'machinery', 'dialogue', 'cinematic', 'voice'];

export type Route = 'game' | 'voice' | 'cinematic';
/** The sliders a player sees (Options → Sound). `dialogue` keeps its own, older setting (atlas:voices-volume). */
export type VolumeGroup = 'master' | 'music' | 'effects' | 'ambience' | 'ui' | 'dialogue' | 'voicechat' | 'cinematic';
export const VOLUME_GROUPS: readonly VolumeGroup[] = ['master', 'music', 'effects', 'ambience', 'ui', 'dialogue', 'voicechat', 'cinematic'];

export interface BusSpec {
  route: Route;
  group: Exclude<VolumeGroup, 'master'>;
  /** Passes the world sub-mix: the underwater low-pass and the room reverb send. */
  world: boolean;
  /** Room reverb send level (linear) when an environment is active; 0 = always dry. */
  reverbSend: number;
  /** Share of the tier's real-voice budget this category may hold (a ceiling, not a reservation). */
  capShare: number;
  /** Voices of this bus that no OTHER bus may steal (protected minimum). */
  reserve: number;
  /**
   * Static level of the bus. NOT a mix decision — a record of the old one: the sfx and ambience owners ran a 0.9
   * master while music and the zones ran 1.0, and every gain in the game was tuned by ear against that. Unity
   * everywhere would have shifted the whole balance by ~1 dB without anyone choosing to.
   */
  trim: number;
}

export const BUS_SPEC: Record<Bus, BusSpec> = {
  ui:          { route: 'game',      group: 'ui',        world: false, reverbSend: 0,    capShare: 0.15, reserve: 2, trim: 0.9 },
  music:       { route: 'game',      group: 'music',     world: false, reverbSend: 0,    capShare: 0.10, reserve: 2, trim: 1 },
  ambience:    { route: 'game',      group: 'ambience',  world: false, reverbSend: 0,    capShare: 0.25, reserve: 2, trim: 0.9 },
  weather:     { route: 'game',      group: 'ambience',  world: false, reverbSend: 0,    capShare: 0.20, reserve: 0, trim: 0.9 },
  playerFoley: { route: 'game',      group: 'effects',   world: true,  reverbSend: 0.55, capShare: 0.20, reserve: 2, trim: 0.9 },
  npcFoley:    { route: 'game',      group: 'effects',   world: true,  reverbSend: 0.6,  capShare: 0.25, reserve: 0, trim: 0.9 },
  combat:      { route: 'game',      group: 'effects',   world: true,  reverbSend: 0.5,  capShare: 0.55, reserve: 3, trim: 0.9 },
  vehicles:    { route: 'game',      group: 'effects',   world: true,  reverbSend: 0,    capShare: 0.25, reserve: 1, trim: 0.9 },
  machinery:   { route: 'game',      group: 'effects',   world: true,  reverbSend: 0.45, capShare: 0.20, reserve: 0, trim: 0.9 },
  dialogue:    { route: 'game',      group: 'dialogue',  world: false, reverbSend: 0,    capShare: 0.10, reserve: 2, trim: 0.9 },
  cinematic:   { route: 'cinematic', group: 'cinematic', world: false, reverbSend: 0,    capShare: 0.05, reserve: 0, trim: 1 },
  voice:       { route: 'voice',     group: 'voicechat', world: false, reverbSend: 0,    capShare: 0.10, reserve: 0, trim: 1 },
};

// ─── Legacy key → bus + priority ─────────────────────────────────────────────

interface KeyRule { test: RegExp; bus: Bus; priority: number }

/**
 * ORDERED — first match wins, so the specific rules stand above the families
 * they carve out of (`sfx_ship_cannon` is combat before `sfx_ship_` is a vehicle).
 * Priority 0..100: what survives when the allocator must choose. 90+ is the
 * player's own action and the alarms; a hunter's commit tell is never recycled.
 */
const KEY_RULES: readonly KeyRule[] = [
  // The interface and the alerts a player must never lose.
  { test: /^ui_/, bus: 'ui', priority: 60 },
  { test: /^sfx_(missile_warning|hull_alarm|low_hp)$/, bus: 'ui', priority: 92 },
  { test: /^sfx_(notify|error|kill_confirm|target_lock|scan_ping|wave_greet)$/, bus: 'ui', priority: 62 },
  { test: /^sfx_(coin_|craft_|loot_pickup|pickup|harvest|ore_yield|equip)/, bus: 'ui', priority: 58 },
  // Your own body.
  { test: /^sfx_player_(hurt|death)$/, bus: 'playerFoley', priority: 95 },
  { test: /^sfx_(respawn|heal_use)$/, bus: 'playerFoley', priority: 70 },
  { test: /^(steps_|foot_)/, bus: 'playerFoley', priority: 45 },
  { test: /^sfx_(jump|land|water_splash)$/, bus: 'playerFoley', priority: 55 },
  // Your body in the water (the water-interaction wave, 2026-10-05): a swim stroke, the climb-out's sheeting water.
  // Another traveler's stroke plays on npcFoley through the call's own `bus` (the footfall pattern).
  { test: /^sfx_(swim_stroke|water_climbout)$/, bus: 'playerFoley', priority: 52 },
  // The hunter: evidence is Foley, the commit tell and the blows are combat — and protected.
  { test: /^sfx_(hunter_ready|stalker_sting)$/, bus: 'combat', priority: 94 },
  { test: /^sfx_(hunter_strike|stalker_drone)$/, bus: 'combat', priority: 85 },
  { test: /^sfx_hunter_/, bus: 'npcFoley', priority: 78 },
  // Your own weapon — voiced at input time, before the server answers.
  { test: /^sfx_(gun_|melee_(?!clash)|weapon_|dry_fire|reload_|recharge_)/, bus: 'combat', priority: 90 },
  { test: /^sfx_creature_/, bus: 'combat', priority: 80 },
  { test: /^sfx_impact_/, bus: 'combat', priority: 70 },
  { test: /^sfx_(enemy_cannon|capital_gun|point_defense|missile|countermeasures|hull_hit|shield_|bolt_|blaster_)/, bus: 'combat', priority: 74 },
  { test: /^sfx_ship_(cannon|explosion|impact|crash)$/, bus: 'combat', priority: 76 },
  // A shot meeting the water (2026-10-05): a slug's plop, an energy bolt's hiss — part of the fight, under a hit's voice.
  { test: /^sfx_water_(plop|hiss)$/, bus: 'combat', priority: 68 },
  { test: /^sfx_(battle_|melee_clash|fleet_battle|war_)/, bus: 'combat', priority: 40 },
  // Craft.
  // A hovercraft (or a ship) meeting a wall (physics follow-up A, 2026-10-05): the thunk takes and the grind loop are
  // YOUR craft's contact — vehicles, a notch above the engines so a crash is never the voice the allocator drops.
  { test: /^sfx_hover_(impact|scrape)$/, bus: 'vehicles', priority: 74 },
  { test: /^sfx_(hover_engine|space_engine|cloud_rush|mining_laser|freighter_engine)$/, bus: 'vehicles', priority: 72 },
  // A craft on the water (2026-10-05): the cushion's spray hiss, a ship's downwash roar — yours head-locked, others' placed.
  { test: /^sfx_water_(spray|downwash)$/, bus: 'vehicles', priority: 64 },
  { test: /^sfx_(ship_|hyper_|warp_|freighter_warp|escape_pod|cockpit_seat)/, bus: 'vehicles', priority: 66 },
  // The sky and the living world.
  { test: /^(weather_|sfx_thunder)/, bus: 'weather', priority: 55 },
  { test: /^(ambient_|versona_murmur)/, bus: 'ambience', priority: 50 },
  // Running and standing water (lib/audio/water_audio.ts): part of the place, like a bed — never in the under-water world mix.
  { test: /^water_/, bus: 'ambience', priority: 46 },
  // Town life (2026-09-24): fireworks break over the town — part of the place, never the player's own action.
  { test: /^sfx_firework_/, bus: 'ambience', priority: 38 },
  // Fire realism (2026-09-25, the fire agent): a fire's crackle POPS (lib/vfx/fire_system.ts) are part of the place.
  { test: /^sfx_fire_/, bus: 'ambience', priority: 36 },
  { test: /^sfx_(bird_|bugs_|critter_|crowd_|civic_bell)/, bus: 'ambience', priority: 22 },
  // Things people built.
  { test: /^sfx_(door_|button_press|teleport|droid_|structure_place|furniture_place)/, bus: 'machinery', priority: 52 },
  // The lift in a tall building (the interiors programme, slice L): its doors, its hum while it runs, its arrival chime.
  { test: /^sfx_lift_/, bus: 'machinery', priority: 50 },
];

export interface KeyClass { bus: Bus; priority: number; matched: boolean }
const DEFAULT_CLASS: KeyClass = { bus: 'machinery', priority: 45, matched: false };

const classCache = new Map<string, KeyClass>();
/** Where a clip key plays and how much it matters. An unknown key still plays (mid priority) — `matched` false flags it. */
export function classifyKey(key: string): KeyClass {
  let c = classCache.get(key);
  if (!c) {
    c = DEFAULT_CLASS;
    for (const r of KEY_RULES) if (r.test.test(key)) { c = { bus: r.bus, priority: r.priority, matched: true }; break; }
    classCache.set(key, c);
  }
  return c;
}

// ─── Budgets by quality tier ─────────────────────────────────────────────────

export interface AudioBudget {
  /** Real (rendering) voices, every bus together — release tails included. */
  voices: number;
  /** Voices that may use the HRTF panner at once (a per-source convolution). */
  hrtf: number;
  /** Shared convolution reverbs alive at once (a retiring tail counts). */
  convolvers: number;
  /** Obstruction rays per second, all sources together. */
  raysPerSec: number;
  /** Decoded PCM the asset store may hold (MiB) before it evicts the unpinned. */
  decodedMiB: number;
}

/**
 * PROPOSED budgets (the research report's table) — targets to profile, not
 * measurements. Atlas's two cheapest tiers share the "potato" row.
 */
export const AUDIO_BUDGETS: Record<string, AudioBudget> = {
  minimal: { voices: 24, hrtf: 0, convolvers: 0, raysPerSec: 40, decodedMiB: 48 },
  potato:  { voices: 24, hrtf: 0, convolvers: 0, raysPerSec: 40, decodedMiB: 48 },
  low:     { voices: 40, hrtf: 8, convolvers: 1, raysPerSec: 100, decodedMiB: 80 },
  med:     { voices: 64, hrtf: 16, convolvers: 1, raysPerSec: 240, decodedMiB: 112 },
  high:    { voices: 96, hrtf: 24, convolvers: 1, raysPerSec: 480, decodedMiB: 176 },
  ultra:   { voices: 128, hrtf: 32, convolvers: 1, raysPerSec: 800, decodedMiB: 256 },
};
export function budgetFor(tier: string): AudioBudget { return AUDIO_BUDGETS[tier] ?? AUDIO_BUDGETS.med; }

/** A bus's voice ceiling under a budget (never below its own reserve + 1). */
export function busCap(bus: Bus, budget: AudioBudget): number {
  const s = BUS_SPEC[bus];
  return Math.max(s.reserve + 1, Math.round(budget.voices * s.capShare));
}

/** Simultaneous instances of ONE key (voice limiting per sound — the oldest yields). */
export function keyInstanceCap(key: string): number {
  if (/^sfx_(battle_|melee_clash|bolt_|enemy_cannon|blaster_)/.test(key)) return 3;
  if (/^(sfx_impact_|foot_|sfx_bird_)/.test(key)) return 4;
  return 6;
}

// ─── What must be ready before the first trigger ─────────────────────────────

/**
 * THE FIRST-PLAY MISS, closed: `sfx(key)` on an undecoded key used to start the
 * fetch and play NOTHING, so the first shot, the first click and the first hurt
 * of every session were silent. These keys are decoded the moment the context
 * is born — long before a player can reach a trigger. (Per-weapon voices are
 * preloaded by the combat HUD when the ring changes.)
 */
export const CRITICAL_KEYS: readonly string[] = [
  'ui_click', 'ui_open', 'ui_close', 'sfx_notify', 'sfx_error',
  'sfx_player_hurt', 'sfx_player_death', 'sfx_respawn', 'sfx_jump', 'sfx_land', 'sfx_water_splash',
  'sfx_weapon_swing', 'sfx_dry_fire', 'sfx_equip', 'sfx_reload_magazine',
  'sfx_impact_energy', 'sfx_impact_slug', 'sfx_creature_attack', 'sfx_creature_hit',
  'sfx_button_press', 'sfx_door_open', 'sfx_door_close', 'sfx_pickup', 'sfx_teleport',
];

/** Storage keys (localStorage). `dialogue` rides the older versona-voices setting. */
export const VOLUME_STORAGE: Record<VolumeGroup, string> = {
  master: 'atlas:audio:master', music: 'atlas:audio:music', effects: 'atlas:audio:effects', ambience: 'atlas:audio:ambience',
  ui: 'atlas:audio:ui', dialogue: 'atlas:voices-volume', voicechat: 'atlas:audio:voicechat', cinematic: 'atlas:audio:cinematic',
};
export const VOLUME_DEFAULT: Record<VolumeGroup, number> = { master: 1, music: 1, effects: 1, ambience: 1, ui: 1, dialogue: 0.9, voicechat: 1, cinematic: 1 };
export const VOLUME_LABEL: Record<VolumeGroup, string> = {
  master: 'Master volume', music: 'Music', effects: 'Effects (combat, vehicles, footsteps, machines)', ambience: 'Ambience & weather',
  ui: 'Interface', dialogue: 'Dialogue (versona voices)', voicechat: 'Voice chat', cinematic: 'Cinematics (intro)',
};

export type OutputMode = 'headphones' | 'speakers' | 'mono';
export const OUTPUT_MODES: readonly OutputMode[] = ['headphones', 'speakers', 'mono'];
